import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Mail, Download, Calendar } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { emailService } from '@core/supabase/services/emailService';
import { interviewEventService } from '@core/supabase/services/interviewEventService';
import { downloadICS } from '@core/utils/icsGenerator';
import { ApplicationStatus } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';

type ViewMode = 'month' | 'week' | 'day';

type SchedulerItemType = 'followup' | 'interview' | 'scheduled_interview';

interface SchedulerItem {
  id: string;
  type: SchedulerItemType;
  date: string;
  title: string;
  sub: string;
  applicationId: string;
  /** ISO start for calendar export (scheduled interviews + precise follow-ups) */
  startsAt?: string;
}

const INTERVIEW_STATUSES: ApplicationStatus[] = [
  'interview_1_scheduled', 'interview_1_conducted', 'interview_2_scheduled', 'interview_2_conducted',
];

function localDateKey(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calendarDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function SchedulerPage() {
  const { user } = useAuth();
  const [view, setView] = useState<ViewMode>('week');
  const [cursor, setCursor] = useState(() => new Date());
  const [items, setItems] = useState<SchedulerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState<SchedulerItem | null>(null);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    const rangeStart = new Date();
    rangeStart.setDate(rangeStart.getDate() - 60);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date();
    rangeEnd.setDate(rangeEnd.getDate() + 150);
    rangeEnd.setHours(23, 59, 59, 999);

    Promise.all([
      applicationService.list(user.orgId),
      emailService.getUpcomingFollowups(user.orgId),
      interviewEventService.listScheduledInRange(user.orgId, rangeStart, rangeEnd),
    ])
      .then(([apps, followups, scheduled]) => {
        const out: SchedulerItem[] = [];
        apps.forEach((a) => {
          if (INTERVIEW_STATUSES.includes(a.status)) {
            out.push({
              id: `app-${a.id}`,
              type: 'interview',
              date: localDateKey(a.status_updated_at),
              title: `${a.candidate?.full_name ?? 'Candidate'} — ${a.job?.title ?? 'Role'}`,
              sub: a.client?.name ?? '',
              applicationId: a.id,
            });
          }
        });
        followups.forEach((f) => {
          out.push({
            id: `f-${f.id}`,
            type: 'followup',
            date: localDateKey(f.next_followup_at),
            title: f.subject,
            sub: f.recipient_name ?? '',
            applicationId: f.application_id,
            startsAt: f.next_followup_at,
          });
        });
        scheduled.forEach((ev) => {
          const cand = ev.application?.candidate?.full_name ?? 'Candidate';
          const role = ev.application?.job?.title ?? 'Role';
          const t = new Date(ev.scheduled_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
          out.push({
            id: `ie-${ev.id}`,
            type: 'scheduled_interview',
            date: localDateKey(ev.scheduled_at),
            title: `${t} · R${ev.round} · ${cand} — ${role}`,
            sub: ev.application?.client?.name ?? '',
            applicationId: ev.application_id,
            startsAt: ev.scheduled_at,
          });
        });
        setItems(out);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  const cursorStart = new Date(cursor);
  if (view === 'week') cursorStart.setDate(cursorStart.getDate() - cursorStart.getDay());
  if (view === 'month') cursorStart.setDate(1);

  const days: Date[] = [];
  const count = view === 'month' ? 35 : view === 'week' ? 7 : 1;
  for (let i = 0; i < count; i++) {
    const d = new Date(cursorStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const itemsByDate = items.reduce<Record<string, SchedulerItem[]>>((acc, it) => {
    const key = it.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(it);
    return acc;
  }, {});

  const isToday = (d: Date) => calendarDayKey(d) === calendarDayKey(new Date());

  const handleDayDrop = (targetDate: string) => {
    if (!draggedItem || draggedItem.type === 'scheduled_interview') return;
    setItems((prev) =>
      prev.map((it) => (it.id === draggedItem.id ? { ...it, date: targetDate } : it))
    );
    setDraggedItem(null);
  };

  const itemStyle = (t: SchedulerItemType) => {
    if (t === 'followup') return 'bg-yellow-900/40 text-yellow-300';
    if (t === 'scheduled_interview') return 'bg-emerald-900/40 text-emerald-200';
    return 'bg-brand-900/40 text-brand-300';
  };

  return (
    <div>
      <PageHeader
        icon={Calendar}
        breadcrumb={[{ label: 'Pipeline', href: '/scheduler' }, { label: 'Scheduler' }]}
        title="Scheduler"
        subtitle="Follow-ups and scheduled interviews from the pipeline. Use Calendar & notes for personal slots; interview detail lives on each application."
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('scheduler')}
            onExport={(fmt, scope) => listExportNotImplemented('Scheduler', fmt, scope)}
          />
        }
        action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            className="btn-secondary text-xs"
            title="Download visible scheduled items as .ics"
            onClick={() => {
              const events = items
                .filter((it) => it.startsAt && (it.type === 'scheduled_interview' || it.type === 'followup'))
                .map((it) => ({
                  uid: it.id,
                  summary: it.title.replace(/^[\d:]+\s*·\s*R\d+\s*·\s*/, '').trim() || it.title,
                  description: it.sub,
                  dtstart: new Date(it.startsAt!),
                }));
              if (events.length === 0) return;
              downloadICS(`scheduler_${new Date().toISOString().slice(0, 10)}`, events);
            }}
          >
            <Download className="w-3.5 h-3.5" /> Export .ics
          </button>
          <div className="flex rounded-lg border vscode-border overflow-hidden">
            {(['day', 'week', 'month'] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm capitalize ${view === v ? 'bg-brand-600 text-white' : 'vscode-text-tertiary hover:vscode-bg-hover'}`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCursor(new Date(cursor.getTime() - (view === 'month' ? 2629800000 : view === 'week' ? 604800000 : 86400000)))} className="p-1.5 vscode-text-tertiary hover:vscode-text-primary">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm vscode-text-secondary min-w-[140px] text-center">
              {view === 'month' && `${cursor.toLocaleString('default', { month: 'long', year: 'numeric' })}`}
              {view === 'week' && `Week of ${cursorStart.toLocaleDateString()}`}
              {view === 'day' && cursor.toLocaleDateString()}
            </span>
            <button onClick={() => setCursor(new Date(cursor.getTime() + (view === 'month' ? 2629800000 : view === 'week' ? 604800000 : 86400000)))} className="p-1.5 vscode-text-tertiary hover:vscode-text-primary">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        }
      />

      {loading ? (
        <div className="card animate-pulse h-64" />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className={`grid gap-px bg-[var(--vscode-border-primary)] ${view === 'month' ? 'grid-cols-7' : view === 'week' ? 'grid-cols-7' : 'grid-cols-1'}`}>
            {days.map((d) => {
              const key = calendarDayKey(d);
              const dayItems = itemsByDate[key] ?? [];
              return (
                <div
                  key={key}
                  className={`min-h-[100px] vscode-bg-secondary p-2 ${isToday(d) ? 'ring-1 ring-brand-500' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-1', 'ring-green-500'); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('ring-1', 'ring-green-500')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('ring-1', 'ring-green-500');
                    handleDayDrop(key);
                  }}
                >
                  <p className={`text-xs font-medium mb-1 ${isToday(d) ? 'text-brand-400' : 'vscode-text-tertiary'}`}>
                    {d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                  <div className="space-y-1">
                    {dayItems.slice(0, 5).map((it) => (
                      <div
                        key={it.id}
                        draggable={it.type !== 'scheduled_interview'}
                        onDragStart={() => setDraggedItem(it)}
                        onDragEnd={() => setDraggedItem(null)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs truncate cursor-grab active:cursor-grabbing ${
                          itemStyle(it.type)
                        } ${draggedItem?.id === it.id ? 'opacity-60' : ''} ${it.type === 'scheduled_interview' ? 'cursor-default' : ''}`}
                      >
                        {it.type === 'followup' && <Mail className="w-3 h-3 flex-shrink-0" />}
                        {it.type === 'scheduled_interview' && <Calendar className="w-3 h-3 flex-shrink-0 opacity-80" />}
                        <Link
                          to={`/applications/${it.applicationId}`}
                          className="flex-1 truncate hover:underline"
                          title={it.title}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {it.title}
                        </Link>
                      </div>
                    ))}
                    {dayItems.length > 5 && <span className="text-xs vscode-text-tertiary">+{dayItems.length - 5} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-4 text-sm vscode-text-tertiary">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-brand-500" /> Pipeline interview stage</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" /> Scheduled interview</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-yellow-500" /> Follow-ups</span>
      </div>

    </div>
  );
}
